/**
 * Procedural sound.
 *
 * Every effect here is synthesised at runtime with the Web Audio API — there
 * are no sample files in the repository, so there is no licensing question and
 * nothing to download before the first duel. The trade-off is honest: these are
 * stylised synth cues, not recorded foley. See the README's asset notes.
 */
import { useSettings } from '../gameplay/settingsStore';

class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private suspended = false;

  /** Must be called from a user gesture; browsers block audio otherwise. */
  unlock() {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = useSettings.getState().masterVolume;
    this.master.connect(this.ctx.destination);

    // One second of white noise, reused for every impact and shatter.
    const length = this.ctx.sampleRate;
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buffer;

    useSettings.subscribe((s) => {
      if (this.master) this.master.gain.value = s.masterVolume;
    });
  }

  suspend() {
    if (!this.ctx || this.suspended) return;
    this.suspended = true;
    void this.ctx.suspend();
  }

  resume() {
    if (!this.ctx || !this.suspended) return;
    this.suspended = false;
    void this.ctx.resume();
  }

  private now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  private noise(duration: number, gain: number, filterHz: number, q = 1): AudioNode | null {
    if (!this.ctx || !this.master || !this.noiseBuffer) return null;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = filterHz;
    filter.Q.value = q;
    const env = this.ctx.createGain();
    const t = this.now();
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(gain, t + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    src.connect(filter).connect(env).connect(this.master);
    src.start(t);
    src.stop(t + duration + 0.05);
    return env;
  }

  private tone(
    freq: number,
    duration: number,
    gain: number,
    type: OscillatorType = 'sine',
    sweepTo?: number,
  ) {
    if (!this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    osc.type = type;
    const env = this.ctx.createGain();
    const t = this.now();
    osc.frequency.setValueAtTime(freq, t);
    if (sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(20, sweepTo), t + duration);
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(gain, t + 0.01);
    env.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.connect(env).connect(this.master);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }

  /** Firing a bolt: short upward whoosh with a bright transient. */
  cast(intensity = 1) {
    this.tone(180 + 120 * intensity, 0.22, 0.09 * intensity, 'triangle', 620);
    this.noise(0.18, 0.05 * intensity, 1800, 0.8);
  }

  /** A heavier signature spell leaving the wand. */
  signature(intensity = 1) {
    this.tone(90, 0.5, 0.14 * intensity, 'sawtooth', 320);
    this.tone(420, 0.35, 0.06 * intensity, 'sine', 900);
    this.noise(0.4, 0.07 * intensity, 900, 0.6);
  }

  /** Taking a hit: low body thump plus grit. */
  impact(intensity = 1) {
    this.tone(64, 0.45, 0.22 * intensity, 'sine', 30);
    this.noise(0.3, 0.14 * intensity, 420, 0.5);
  }

  /** The parry: glass-shatter transient over a sub drop. */
  parry(quality: 'Weak' | 'Normal' | 'Perfect') {
    const gain = quality === 'Perfect' ? 1 : quality === 'Normal' ? 0.75 : 0.5;
    this.tone(52, 0.7, 0.26 * gain, 'sine', 26);
    this.noise(0.5, 0.16 * gain, 3200, 3);
    this.noise(0.28, 0.1 * gain, 6400, 6);
    this.tone(880, 0.3, 0.05 * gain, 'triangle', 1760);
  }

  /** The magic circle forming in front of the caster. */
  circle() {
    this.tone(320, 0.6, 0.05, 'sine', 480);
    this.tone(160, 0.6, 0.04, 'sine', 240);
  }

  /** Incoming-attack alarm. Deliberately dry so it reads through the mix. */
  warning() {
    this.tone(1200, 0.09, 0.05, 'square');
  }

  /**
   * The parry window opening. A single dry tick, pitched well above the duel's
   * low-end so it cuts through an exchange — this is a metronome beat, not an
   * effect, and it has to be heard while the player is speaking over it.
   */
  parryWindow() {
    this.tone(1560, 0.05, 0.055, 'triangle');
  }

  /** Last chance: the shot has arrived and is holding. Urgent, still dry. */
  parryLastChance() {
    this.tone(2100, 0.04, 0.05, 'square');
  }

  countdown(final = false) {
    this.tone(final ? 880 : 520, final ? 0.4 : 0.14, 0.08, 'triangle');
  }

  ui() {
    this.tone(660, 0.06, 0.035, 'triangle');
  }

  fail() {
    this.tone(150, 0.25, 0.07, 'sawtooth', 70);
  }
}

export const sfx = new Sfx();
